import React from 'react';
import { Typography, DatePicker, Select, Checkbox } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { CustomTooltip } from './custom-tooltip';
import type { BaseFieldProps, FieldChangeHandler } from '../../../types/form-renderer';
import type { CalendarField as CalendarFieldType, RoutedFormData } from '../../../types/templates';

const { Text } = Typography;

const DAYS_OF_WEEK = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'] as const;

type Props = BaseFieldProps<CalendarFieldType> & {
  formData?: RoutedFormData;
  onFieldChange?: FieldChangeHandler;
};

export const CalendarField: React.FC<Props> = ({
  field,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error,
  formData,
  onFieldChange
}) => {
  const today = dayjs();
  const [isLive, setIsLive] = React.useState(true);
  const showLiveOption = field.showLiveOption ?? false;

  const handleDateChange = (date: Dayjs | null) => {
    if (!date) return;
    onChange(date.format('YYYY-MM-DD'));

    if (onFieldChange && showLiveOption) {
      const dayName = DAYS_OF_WEEK[date.day()];
      onFieldChange('day', isLive ? `${dayName} - en vivo` : dayName);
    }
  };

  const handleLiveChange = (checked: boolean) => {
    setIsLive(checked);
    if (onFieldChange && value) {
      const dayName = DAYS_OF_WEEK[dayjs(value, 'YYYY-MM-DD').day()];
      onFieldChange('day', checked ? `${dayName} - en vivo` : dayName);
    }
  };

  const dateValue = value ? dayjs(value, 'YYYY-MM-DD') : today;
  const disabledDate = (current: Dayjs) => current && current.isBefore(today, 'day');

  const showDayField = Boolean(showLiveOption && formData && onFieldChange);
  const dayValue = formData?.day as string | undefined;

  const dayOptions = DAYS_OF_WEEK.map(day => {
    const label = isLive ? `${day} - en vivo` : day;
    return { value: label, label };
  });

  React.useEffect(() => {
    if (value && onFieldChange && showLiveOption) {
      const dayName = DAYS_OF_WEEK[dayjs(value, 'YYYY-MM-DD').day()];
      const finalDayValue = isLive ? `${dayName} - en vivo` : dayName;
      if (formData?.day !== finalDayValue) {
        onFieldChange('day', finalDayValue);
      }
    }
  }, [value, isLive, onFieldChange, showLiveOption, formData]);

  return (
    <div
      style={{
        ...commonStyle,
        flexDirection: 'row',
        marginBottom: 0,
        gap: 20,
        alignItems: 'center',
        width: '100%'
      }}
    >
      <div style={{ ...commonStyle, width: 150 }}>
        <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
          {field.label}
        </Text>
        <CustomTooltip field={field}>
          <DatePicker
            placeholder={field.placeholder || 'Seleccionar fecha'}
            style={{ width: '100%', fontSize: 12 }}
            value={dateValue}
            onChange={handleDateChange}
            format='YYYY-MM-DD'
            status={error ? 'error' : undefined}
            disabledDate={disabledDate}
            allowClear={false}
            inputReadOnly
          />
        </CustomTooltip>
        {error && (
          <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
            {error}
          </Text>
        )}
      </div>

      {showDayField && onFieldChange && (
        <div style={{ ...commonStyle, width: 150 }}>
          <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
            Día
          </Text>
          <Select
            placeholder='Día de la semana'
            style={{ width: '100%', textAlign: 'left' }}
            options={dayOptions}
            value={dayValue}
            onChange={v => onFieldChange('day', v)}
            disabled
          />
        </div>
      )}

      {showDayField && (
        <div style={{ ...commonStyle, justifyContent: 'flex-end', paddingTop: 30, width: 150 }}>
          <Checkbox checked={isLive} onChange={e => handleLiveChange(e.target.checked)}>
            <Text style={{ fontSize: 12 }}>En vivo</Text>
          </Checkbox>
        </div>
      )}
    </div>
  );
};
